const IMAGE_SELECTOR = '.product-image-button img, .product-gallery-stage .product-zoom-link img';

export function createHeroSlideshow(root, environment = globalThis) {
  const slides = [...root.querySelectorAll('[data-hero-slide]')];
  const controls = root.querySelector('[data-hero-controls]');
  if (slides.length < 2 || !controls) return { destroy() {} };
  const document = root.ownerDocument;
  const media = environment.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = environment.navigator?.connection;
  const previous = root.querySelector('[data-hero-previous]');
  const next = root.querySelector('[data-hero-next]');
  const pause = root.querySelector('[data-hero-pause]');
  const count = root.querySelector('[data-hero-count]');
  const status = root.querySelector('[data-hero-status]');
  const removers = [];
  let index = 0;
  let userPaused = false;
  let focused = root.contains(document.activeElement);
  let visible = !environment.IntersectionObserver;
  let destroyed = false;
  let timer = null;
  const listen = (target, name, callback) => {
    target?.addEventListener?.(name, callback);
    removers.push(() => target?.removeEventListener?.(name, callback));
  };
  const motionAllowed = () => !media?.matches && !connection?.saveData;
  const clear = () => {
    if (timer !== null) environment.clearTimeout(timer);
    timer = null;
  };
  const schedule = () => {
    clear();
    const playing = !destroyed && root.dataset.autoplay === 'true' && !userPaused && !focused && visible && document.visibilityState === 'visible' && motionAllowed();
    root.dataset.heroMotionPaused = String(!playing);
    if (playing) {
      timer = environment.setTimeout(() => show(index + 1), 3000);
    }
    if (pause) {
      pause.hidden = !motionAllowed();
      pause.dataset.paused = String(userPaused);
      pause.setAttribute('aria-label', userPaused ? root.dataset.playLabel : root.dataset.pauseLabel);
    }
  };
  const show = (requested, manual = false) => {
    if (destroyed) return;
    index = (requested + slides.length) % slides.length;
    slides.forEach((slide, position) => {
      const active = position === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    if (count) count.textContent = `${index + 1} / ${slides.length}`;
    if (manual && status) status.textContent = slides[index].getAttribute('aria-label');
    schedule();
  };
  listen(previous, 'click', () => show(index - 1, true));
  listen(next, 'click', () => show(index + 1, true));
  listen(pause, 'click', () => { userPaused = !userPaused; schedule(); });
  listen(root, 'focusin', () => { focused = true; schedule(); });
  listen(root, 'focusout', (event) => { focused = root.contains(event.relatedTarget); schedule(); });
  listen(document, 'visibilitychange', schedule);
  listen(media, 'change', schedule);
  listen(connection, 'change', schedule);
  const observer = environment.IntersectionObserver ? new environment.IntersectionObserver((entries) => {
    visible = entries.some((entry) => entry.target === root && entry.isIntersecting && entry.intersectionRatio >= 0.1);
    schedule();
  }, { threshold: [0, 0.1] }) : null;
  observer?.observe(root);
  controls.hidden = false;
  show(0);
  return {
    destroy() {
      destroyed = true;
      root.dataset.heroMotionPaused = 'true';
      clear();
      observer?.disconnect();
      removers.forEach((remove) => remove());
    }
  };
}

export function createImageMotion(document, environment = globalThis) {
  const media = environment.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = environment.navigator?.connection;
  const records = new WeakMap();
  const images = new Set();
  let stopped = false;
  const motionAllowed = () => !media?.matches && !connection?.saveData && !stopped;
  const observer = environment.IntersectionObserver ? new environment.IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const record = records.get(entry.target);
      if (!record) return;
      record.visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
      settle(entry.target, record);
    });
  }, { threshold: [0, 0.15] }) : null;
  const settle = (image, record) => {
    if (!record.loaded || !record.visible || record.started || !motionAllowed()) return;
    record.started = true;
    observer?.unobserve(image);
    environment.requestAnimationFrame(() => environment.requestAnimationFrame(() => {
      if (motionAllowed() && image.isConnected) image.classList.add('is-settled');
    }));
  };
  const add = (image) => {
    if (records.has(image) || !observer || !motionAllowed()) return;
    const record = { loaded: image.complete && image.naturalWidth > 0, visible: false, started: false };
    records.set(image, record);
    images.add(image);
    image.classList.add('tizico-image-motion');
    image.addEventListener('load', () => { record.loaded = true; settle(image, record); }, { once: true });
    observer.observe(image);
  };
  const scan = (scope) => {
    if (scope.matches?.(IMAGE_SELECTOR)) add(scope);
    scope.querySelectorAll?.(IMAGE_SELECTOR).forEach(add);
  };
  const disable = () => {
    if (motionAllowed()) return;
    images.forEach((image) => image.classList.remove('tizico-image-motion', 'is-settled'));
    observer?.disconnect();
  };
  media?.addEventListener?.('change', disable);
  connection?.addEventListener?.('change', disable);
  scan(document);
  return {
    scan,
    destroy() {
      stopped = true;
      disable();
      media?.removeEventListener?.('change', disable);
      connection?.removeEventListener?.('change', disable);
    }
  };
}

if (typeof document !== 'undefined') {
  const heroControllers = new Map();
  const imageMotion = createImageMotion(document);
  const initialize = (scope) => {
    const heroes = [...scope.querySelectorAll('[data-tizico-hero]')];
    if (scope.matches?.('[data-tizico-hero]')) heroes.unshift(scope);
    heroes.forEach((hero) => {
      if (!heroControllers.has(hero)) heroControllers.set(hero, createHeroSlideshow(hero));
    });
    imageMotion.scan(scope);
  };
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    heroControllers.forEach((controller, hero) => {
      if (event.target.contains(hero)) {
        controller.destroy();
        heroControllers.delete(hero);
      }
    });
  });
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) imageMotion.scan(node);
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}
